import React, { useState, useEffect } from 'react';
import { X, Send, Check } from 'lucide-react';
import axios from 'axios';

const InviteViewerModal = ({ isOpen, onClose, businessId, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAvailablePermissions();
    }
  }, [isOpen]);

  const fetchAvailablePermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/viewer/permissions`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setAvailablePermissions(response.data.permissions);
        // Initialize all permissions to false
        const initialPerms = {};
        response.data.permissions.forEach(p => {
          initialPerms[p.key] = false;
        });
        setSelectedPermissions(initialPerms);
      }
    } catch (err) {
      console.error('Error fetching permissions', err);
    }
  };

  const togglePermission = (key) => {
    setSelectedPermissions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleAll = () => {
    const allSelected = Object.values(selectedPermissions).every(v => v);
    const newPerms = {};
    availablePermissions.forEach(p => {
      newPerms[p.key] = !allSelected;
    });
    setSelectedPermissions(newPerms);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/viewer/invite`,
        {
          email,
          business_id: businessId,
          permissions: selectedPermissions
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invitation');
      console.error('Error sending viewer invitation', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSelectedPermissions({});
    setError('');
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  const selectedCount = Object.values(selectedPermissions).filter(v => v).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">Invite Viewer</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <Check className="text-green-600" size={20} />
              <span className="text-green-800">Invitation sent successfully!</span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {error}
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
              placeholder="viewer@example.com"
              required
            />
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Select Permissions ({selectedCount} selected)
              </label>
              <button
                type="button"
                onClick={toggleAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {Object.values(selectedPermissions).every(v => v) ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
              {availablePermissions.map((perm) => (
                <div
                  key={perm.key}
                  className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                  onClick={() => togglePermission(perm.key)}
                >
                  <input
                    type="checkbox"
                    checked={selectedPermissions[perm.key] || false}
                    onChange={() => togglePermission(perm.key)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{perm.label}</div>
                    <div className="text-sm text-gray-600">{perm.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-900 mb-2">Important Notes:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Viewers have <strong>read-only access</strong> - they cannot modify any data</li>
              <li>• Permissions can be updated or revoked at any time</li>
              <li>• The viewer will receive an email with an activation code</li>
            </ul>
          </div>

          {/* Footer */}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || success || selectedCount === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-h-[44px]"
            >
              <Send size={16} />
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteViewerModal;
